/**
 * Shared GLSL chunks and small colour helpers for the screen-space post chain.
 *
 * Every screen filter is a real `Filter` with a hand-written GLSL ES 3.00
 * fragment shader running on Pixi's default filter vertex shader. Pixi injects
 * `#version 300 es` and the precision qualifiers, so the sources below start
 * straight at the declarations.
 *
 * The FilterSystem binds one global uniform group to every pass. Declaring any
 * of its members in a fragment shader is enough to receive it:
 *
 *   uInputSize   (sourceW, sourceH, 1/sourceW, 1/sourceH)   logical units
 *   uInputPixel  (pixelW,  pixelH,  1/pixelW,  1/pixelH)    device pixels
 *   uInputClamp  (minU, minV, maxU, maxV)                   safe sample range
 *   uOutputFrame (originX, originY, frameW, frameH)         logical units
 *
 * `uOutputFrame.zw * uInputSize.zw` is the size of the filtered area in UV
 * space, which is what turns `vTextureCoord` into a clean 0..1 coordinate
 * across the filter area regardless of power-of-two texture padding.
 */

/** Global uniforms provided by the FilterSystem. Include once per shader. */
export const GLSL_GLOBALS = /* glsl */ `
uniform vec4 uInputSize;
uniform vec4 uInputPixel;
uniform vec4 uInputClamp;
uniform vec4 uOutputFrame;
`;

/**
 * Frame helpers. Requires GLSL_GLOBALS.
 *
 * `frameUv` maps a texture coordinate to 0..1 across the filtered area — the
 * letterboxed design rect — so vignette and aberration stay centred on the
 * table rather than on the padded texture.
 */
export const GLSL_FRAME = /* glsl */ `
vec2 frameScale() {
    return uOutputFrame.zw * uInputSize.zw;
}

vec2 frameUv(vec2 tc) {
    return tc / max(frameScale(), vec2(1e-6));
}

float frameAspect() {
    return uOutputFrame.z / max(uOutputFrame.w, 1.0);
}

vec2 clampUv(vec2 tc) {
    return clamp(tc, uInputClamp.xy, uInputClamp.zw);
}
`;

/** Colour maths shared by the bright pass and the grain. */
export const GLSL_COLOUR = /* glsl */ `
float luma(vec3 c) {
    return dot(c, vec3(0.2126, 0.7152, 0.0722));
}

float maxChannel(vec3 c) {
    return max(c.r, max(c.g, c.b));
}

/** Pixi textures are premultiplied; recover the straight colour. */
vec3 unpremultiply(vec4 c) {
    return c.a > 0.0 ? c.rgb / c.a : c.rgb;
}

vec4 premultiply(vec3 rgb, float a) {
    return vec4(clamp(rgb, 0.0, 1.0) * a, a);
}
`;

/** Dave Hoskins' hash13 — cheap, well-distributed, no texture lookup. */
export const GLSL_HASH = /* glsl */ `
float hash13(vec3 p3) {
    p3 = fract(p3 * 0.1031);
    p3 += dot(p3, p3.zyx + 31.32);
    return fract((p3.x + p3.y) * p3.z);
}
`;

/** 0xRRGGBB -> [r, g, b] in 0..1, ready for a `vec3<f32>` uniform. */
export function rgb01(hex: number): [number, number, number] {
  return [((hex >> 16) & 0xff) / 255, ((hex >> 8) & 0xff) / 255, (hex & 0xff) / 255];
}

/** Linear blend between two 0..1 rgb triples. */
export function mixRgb(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
  t: number,
): [number, number, number] {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/** Uniform-ready Float32Array from an rgb triple. */
export function vec3f(rgb: readonly [number, number, number]): Float32Array {
  return new Float32Array([rgb[0], rgb[1], rgb[2]]);
}
